import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Spinner,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
  CardHeader,
  Badge,
} from "reactstrap";
import {
  useGetLiftingPermitHealthDeclarationQuery,
  useGetLiftingPermitHealthDeclarationByIdQuery,
  useUpdateLiftingPermitHealthDeclarationIdMutation,
  useCreateLiftingPermitHealthDeclarationMutation,
} from "slices/pageSettings/pageSettingsSlice";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

interface LiftingPermitHealthDeclarationData {
  id?: number;
  declaration: string;
  text_size: string;
  is_visible: boolean;
}

const validationSchema = Yup.object({
  declaration: Yup.string().required("Declaration is required"),
  text_size: Yup.string().required("Text size is required"),
  is_visible: Yup.boolean().required("Visibility is required"),
});

const LiftingPermitHealthDeclarationTextTab: React.FC = () => {
  const [selectedHealthDeclarationId, setSelectedHealthDeclarationId] =
    useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [healthDeclaration, setHealthDeclaration] =
    useState<LiftingPermitHealthDeclarationData | null>(null);

  const {
    data: healthDeclarationData,
    refetch,
    isLoading,
  } = useGetLiftingPermitHealthDeclarationQuery(undefined);

  const { data: healthDeclarationDetails, isFetching: isDetailFetching } =
    useGetLiftingPermitHealthDeclarationByIdQuery(selectedHealthDeclarationId, {
      skip: !selectedHealthDeclarationId,
    });

  const [updateHealthDeclaration, { isLoading: updateLoading }] =
    useUpdateLiftingPermitHealthDeclarationIdMutation();
  const [createHealthDeclaration, { isLoading: createLoading }] =
    useCreateLiftingPermitHealthDeclarationMutation();

  useEffect(() => {
    if (modalOpen && selectedHealthDeclarationId && healthDeclarationDetails) {
      setHealthDeclaration(healthDeclarationDetails);
    } else if (modalOpen && !selectedHealthDeclarationId) {
      setHealthDeclaration({
        declaration: "",
        text_size: "",
        is_visible: false,
      });
    }
  }, [modalOpen, selectedHealthDeclarationId, healthDeclarationDetails]);

  const toggleModal = () => {
    if (!modalOpen) {
      setHealthDeclaration(null);
    }
    setModalOpen(!modalOpen);
  };

  const handleEditHealthDeclaration = (id: number) => {
    setSelectedHealthDeclarationId(id);
    toggleModal();
  };

  const handleAddHealthDeclaration = () => {
    setSelectedHealthDeclarationId(null);
    toggleModal();
  };

  const handleSaveHealthDeclaration = async (
    values: LiftingPermitHealthDeclarationData
  ) => {
    try {
      if (values.id) {
        await updateHealthDeclaration({
          id: values.id,
          updateData: values,
        }).unwrap();
      } else {
        await createHealthDeclaration(values).unwrap();
      }
      refetch();
      toggleModal();
    } catch (error) {
      console.error("Error saving health declaration:", error);
    }
  };

  return (
    <div className="container mt-4">
      {/* Header with Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        {(!healthDeclarationData || healthDeclarationData.length === 0) && (
          <Button
            color="primary"
            onClick={handleAddHealthDeclaration}
            className="d-flex align-items-center"
          >
            <i className="ri-add-line me-1"></i>
            Add Health Declaration
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center">
          <Spinner color="primary" />
        </div>
      ) : (
        <Row>
          {healthDeclarationData?.length > 0 ? (
            healthDeclarationData.map(
              (item: LiftingPermitHealthDeclarationData) => (
                <Col md={10} sm={8} key={item.id} className="mb-4">
                  <Card>
                    <CardHeader className="d-flex align-items-center">
                      <h4 className="card-title flex-grow-1 mb-0">
                        Health Declaration
                      </h4>
                      <div className="flex-shrink-0">
                        <Button
                          onClick={() => handleEditHealthDeclaration(item.id!)}
                          className="btn btn-soft-info btn-md shadow-none"
                        >
                          Edit
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <div className="table-card">
                        <table className="table mb-0">
                          <tbody>
                            <tr>
                              <td
                                className="fw-medium"
                                style={{ width: "150px" }}
                              >
                                Declaration
                              </td>
                              <td style={{ width: "700px" }}>
                                {item.declaration}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-medium">Text Size</td>
                              <td>{item.text_size}</td>
                            </tr>
                            <tr>
                              <td className="fw-medium">Is Visible</td>
                              <td>
                                {item.is_visible ? (
                                  <Badge className="bg-success">Yes</Badge>
                                ) : (
                                  <Badge className="bg-danger">No</Badge>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              )
            )
          ) : (
            <Col>
              <p className="text-center">No Health Declarations Found</p>
            </Col>
          )}
        </Row>
      )}

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {selectedHealthDeclarationId
            ? "Edit Health Declaration"
            : "Add Health Declaration"}
        </ModalHeader>
        <ModalBody>
          {isDetailFetching && selectedHealthDeclarationId ? (
            <div className="text-center">
              <Spinner color="primary" />
            </div>
          ) : healthDeclaration ? (
            <Formik
              initialValues={healthDeclaration}
              validationSchema={validationSchema}
              onSubmit={handleSaveHealthDeclaration}
              enableReinitialize
            >
              {({ values, handleChange, handleBlur }) => (
                <Form>
                  <FormGroup>
                    <Label for="declaration">
                      Declaration<span className="text-danger">*</span>
                    </Label>
                    <Field
                      as="textarea"
                      rows={4}
                      id="declaration"
                      name="declaration"
                      value={values.declaration}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="form-control mt-2"
                    />
                    <ErrorMessage
                      name="declaration"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="text_size">
                      Text Size<span className="text-danger">*</span>
                    </Label>
                    <Field
                      as={Input}
                      type="text"
                      id="text_size"
                      name="text_size"
                      value={values.text_size}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <ErrorMessage
                      name="text_size"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>

                  <FormGroup check>
                    <Label check>
                      <Field
                        type="checkbox"
                        id="is_visible"
                        name="is_visible"
                        checked={values.is_visible}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />{" "}
                      Is Visible
                    </Label>
                    <ErrorMessage
                      name="is_visible"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                  <ModalFooter>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={updateLoading || createLoading}
                    >
                      {updateLoading || createLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      onClick={toggleModal}
                      disabled={updateLoading || createLoading}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          ) : (
            <div className="text-center">
              <Spinner color="primary" />
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default LiftingPermitHealthDeclarationTextTab;
